/**
 * Cart Protection Script
 *
 * Prevents customers from removing passes when they have dependent class bookings.
 * Supports all pass types: day, month, 3months, 6months, year
 */

document.addEventListener("DOMContentLoaded", function () {
  const originalFetch = window.fetch;

  // Configuration
  const CONFIG = {
    PASS_PRODUCT_IDS: ["8367474737329", "8367474966705"], // Add your pass product IDs here
    PASS_KEYWORDS: ["pass", "Pass"],
    CLASS_KEYWORDS: ["class", "Class"],
  };

  /**
   * Show alert to user
   */
  function showAlert(message, title = "Cannot Remove Pass") {
    if (window.EdgeModal?.alert) {
      window.EdgeModal.alert(message, title);
    } else {
      alert(message);
    }
  }

  /**
   * Get current cart
   */
  async function getCurrentCart() {
    try {
      const response = await originalFetch("/cart.js");
      if (!response.ok)
        throw new Error(`Cart fetch failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Determine if item is a pass
   */
  function isPassItem(item) {
    if (!item) return false;

    // Check by product ID
    if (CONFIG.PASS_PRODUCT_IDS.includes(item.product_id?.toString())) {
      return true;
    }

    // Check by title/handle keywords
    const title = (item.product_title || "").toLowerCase();
    const handle = (item.handle || "").toLowerCase();

    return CONFIG.PASS_KEYWORDS.some(
      (keyword) =>
        title.includes(keyword.toLowerCase()) ||
        handle.includes(keyword.toLowerCase())
    );
  }

  /**
   * Determine if item is a class
   */
  function isClassItem(item) {
    if (!item) return false;

    const title = (item.product_title || "").toLowerCase();
    const type = (item.product_type || "").toLowerCase();

    return CONFIG.CLASS_KEYWORDS.some(
      (keyword) =>
        title.includes(keyword.toLowerCase()) ||
        type.includes(keyword.toLowerCase())
    );
  }

  /**
   * Get pass type from item
   */
  function getPassType(item) {
    if (!item?.properties?._pass_type) {
      // Fallback to title parsing
      const title = (item.product_title || "").toLowerCase();
      if (title.includes("day")) return "day";
      if (title.includes("3 month")) return "3months";
      if (title.includes("6 month")) return "6months";
      if (title.includes("year")) return "year";
      if (title.includes("month")) return "month";
      return "month"; // default
    }
    return item.properties._pass_type;
  }

  /**
   * Extract pass date from item
   */
  function getPassDate(item) {
    if (!item?.properties) return null;

    // Try different property names
    return (
      item.properties.pass_date ||
      item.properties["pass date"] ||
      item.properties._pass_date ||
      null
    );
  }

  /**
   * Extract class date from item
   */
  function getClassDate(item) {
    if (!item?.properties) return null;

    // Handle different date formats
    let date =
      item.properties.Date ||
      item.properties.date ||
      item.properties._date ||
      null;

    if (!date) return null;

    // Convert MM/DD/YYYY to YYYY-MM-DD if needed
    if (date.includes("/")) {
      const [month, day, year] = date.split("/");
      date = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // Handle ISO format (_date_iso8601)
    if (item.properties._date_iso8601) {
      date = item.properties._date_iso8601.split("T")[0];
    }

    return date;
  }

  /**
   * Extract owner info from item
   */
  function getOwnerInfo(item) {
    if (!item?.properties)
      return { email: null, firstName: null, lastName: null };

    return {
      email:
        item.properties.email ||
        item.properties.Email ||
        item.properties._email ||
        null,
      firstName:
        item.properties.first_name || item.properties._first_name || null,
      lastName: item.properties.last_name || item.properties._last_name || null,
    };
  }

  /**
   * Check if class date is covered by pass
   */
  function isDateCoveredByPass(
    classDate,
    passDate,
    passType,
    passExpiration = null
  ) {
    if (!classDate || !passDate) return false;

    const classDateObj = new Date(classDate);
    const passDateObj = new Date(passDate);

    switch (passType) {
      case "day":
        return classDate === passDate;

      case "month":
      case "3months":
      case "6months":
      case "year":
        if (passExpiration) {
          const expirationObj = new Date(passExpiration.split("T")[0]); // Handle ISO format
          return classDateObj >= passDateObj && classDateObj <= expirationObj;
        }
        // Fallback calculation if no expiration provided
        const endDate = new Date(passDateObj);
        switch (passType) {
          case "month":
            endDate.setMonth(endDate.getMonth() + 1);
            break;
          case "3months":
            endDate.setMonth(endDate.getMonth() + 3);
            break;
          case "6months":
            endDate.setMonth(endDate.getMonth() + 6);
            break;
          case "year":
            endDate.setFullYear(endDate.getFullYear() + 1);
            break;
        }
        return classDateObj >= passDateObj && classDateObj <= endDate;

      default:
        return false;
    }
  }

  /**
   * Check if owners match (handles cases where info might be missing)
   */
  function ownersMatch(owner1, owner2) {
    // If either doesn't have complete info, consider it a match (same person scenario)
    if (!owner1.email && !owner1.firstName && !owner1.lastName) return true;
    if (!owner2.email && !owner2.firstName && !owner2.lastName) return true;

    // Email match is strongest indicator
    if (owner1.email && owner2.email) {
      return owner1.email.toLowerCase() === owner2.email.toLowerCase();
    }

    // Name match as fallback
    if (
      owner1.firstName &&
      owner1.lastName &&
      owner2.firstName &&
      owner2.lastName
    ) {
      return (
        owner1.firstName === owner2.firstName &&
        owner1.lastName === owner2.lastName
      );
    }

    // If we can't determine ownership, assume they match (safer for UX)
    return true;
  }

  /**
   * Find cart item being removed
   */
  function findCartItem(cart, changeData) {
    if (!cart?.items?.length) return null;

    // Find by line item (1-based index)
    if (changeData.line) {
      const lineIndex = parseInt(changeData.line, 10) - 1;
      if (lineIndex >= 0 && lineIndex < cart.items.length) {
        return cart.items[lineIndex];
      }
    }

    // Find by variant ID
    if (changeData.id) {
      const variantId = changeData.id.toString().split(":")[0];
      return cart.items.find(
        (item) => item.variant_id?.toString() === variantId
      );
    }

    return null;
  }

  /**
   * Find dependent classes for a pass
   */
  function findDependentClasses(cartItems, passItem) {
    const passType = getPassType(passItem);
    const passDate = getPassDate(passItem);
    const passExpiration = passItem.properties?._pass_expiration;
    const passOwner = getOwnerInfo(passItem);

    if (!passDate) return [];

    const dependentClasses = [];

    cartItems.forEach((item) => {
      if (!isClassItem(item)) return;

      const classDate = getClassDate(item);
      const classOwner = getOwnerInfo(item);

      if (!classDate) return;

      // Check if owners match
      if (!ownersMatch(passOwner, classOwner)) return;

      // Check if class date is covered by this pass
      if (isDateCoveredByPass(classDate, passDate, passType, passExpiration)) {
        dependentClasses.push({
          item,
          classDate,
          title: item.product_title,
        });
      }
    });

    return dependentClasses;
  }

  /**
   * Check if classes are covered by other passes
   */
  function areClassesCoveredByOtherPasses(
    dependentClasses,
    cartItems,
    excludePassItem
  ) {
    if (!dependentClasses.length) return true;

    // Get all other passes in cart
    const otherPasses = cartItems.filter(
      (item) => isPassItem(item) && item.id !== excludePassItem.id
    );

    // Add customer's existing passes
    if (window.customerPasses) {
      window.customerPasses.forEach((pass) => {
        otherPasses.push({
          properties: {
            _pass_type: pass.type,
            pass_date: pass.date,
            _pass_expiration: pass.expiration,
            email: pass.email,
            first_name: pass.first_name,
            last_name: pass.last_name,
          },
        });
      });
    }

    // Check if each dependent class is covered by another pass
    return dependentClasses.every(({ item: classItem, classDate }) => {
      const classOwner = getOwnerInfo(classItem);

      return otherPasses.some((passItem) => {
        const passType = getPassType(passItem);
        const passDate = getPassDate(passItem);
        const passExpiration = passItem.properties?._pass_expiration;
        const passOwner = getOwnerInfo(passItem);

        // Check if owners match and date is covered
        return (
          ownersMatch(classOwner, passOwner) &&
          isDateCoveredByPass(classDate, passDate, passType, passExpiration)
        );
      });
    });
  }

  /**
   * Main function to check pass removal
   */
  async function checkPassRemoval(changeData) {
    try {
      const cart = await getCurrentCart();
      const itemToRemove = findCartItem(cart, changeData);

      if (!itemToRemove) {
        return false;
      }

      if (!isPassItem(itemToRemove)) {
        return false;
      }

      const dependentClasses = findDependentClasses(cart.items, itemToRemove);

      if (!dependentClasses.length) {
        return false;
      }

      // Check if other passes cover these classes
      const allCovered = areClassesCoveredByOtherPasses(
        dependentClasses,
        cart.items,
        itemToRemove
      );

      if (!allCovered) {
        const passOwner = getOwnerInfo(itemToRemove);
        const classNames = dependentClasses.map((c) => c.title).join(", ");

        let message = `This pass cannot be removed because you have classes (${classNames}) that require it. Please remove the class bookings first.`;

        if (passOwner.firstName && passOwner.lastName) {
          message = `This pass for ${passOwner.firstName} ${passOwner.lastName} cannot be removed because there are classes (${classNames}) that require it. Please remove those class bookings first.`;
        }

        showAlert(message);
        return true; // Prevent removal
      }

      return false; // Allow removal
    } catch (error) {
      return false; // Allow removal on error
    }
  }

  /**
   * Intercept cart change requests
   */
  window.fetch = function (url, options) {
    // Skip if explicitly bypassed
    if (options?.__bypassIntercept) {
      delete options.__bypassIntercept;
      return originalFetch.apply(this, arguments);
    }

    // Check for cart change requests (item removal)
    if (
      typeof url === "string" &&
      url.includes("/cart/change") &&
      options?.method === "POST" &&
      typeof options.body === "string"
    ) {
      try {
        const data = JSON.parse(options.body);

        // Check if removing item (quantity = 0)
        if (data.quantity === 0) {
          return checkPassRemoval(data)
            .then((shouldPrevent) => {
              if (shouldPrevent) {
                // Return fake success response
                return {
                  ok: true,
                  status: 200,
                  json: () => Promise.resolve({ status: "blocked" }),
                  text: () => Promise.resolve('{"status":"blocked"}'),
                  headers: new Headers(),
                };
              }
              // Proceed with original request
              return originalFetch.apply(this, arguments);
            })
            .catch((error) => {
              return originalFetch.apply(this, arguments);
            });
        }
      } catch (error) {
        // Continue with original request on parsing error
      }
    }

    // For all other requests, use original fetch
    return originalFetch.apply(this, arguments);
  };
});
